import { useState } from 'react';
import { Box, ButtonBase, Divider, styled, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
    Table,
    TableBody,
    TableCell as _TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import InfoTooltip from 'components/popups/InfoTooltip';
import moment from 'moment-timezone';
import { parseDecimalToPercent } from 'utility/numbers';

export function Incidentals(props) {
    const { incidentals, total_incidentals } = props;

    return (
        <TableContainer>
            <Table
                sx={{
                    '& th:not(:nth-of-type(3)), & td:not(:nth-of-type(3))': {
                        whiteSpace: 'nowrap',
                    },
                }}
            >
                <TableHead>
                    <TableRow>
                        <TableCellHeader>DATE</TableCellHeader>
                        <TableCellHeader>TYPE</TableCellHeader>
                        <TableCellHeader>DESCRIPTION</TableCellHeader>
                        <TableCellHeader align="right">AMOUNT</TableCellHeader>
                        <TableCellHeader align="right">TAX</TableCellHeader>
                        <TableCellHeader align="right">TOTAL</TableCellHeader>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {incidentals.map((i) => (
                        <TableRow key={i.inc_id}>
                            <TableCell size="medium">
                                {moment(i.inc_date).get('year') === moment().get('year')
                                    ? moment(i.inc_date).format('MMM Do')
                                    : moment(i.inc_date).format('MMM Do YYYY')}
                            </TableCell>
                            <TableCell size="medium">{i.type_name}</TableCell>
                            <TableCell size="medium" style={{ minWidth: 180 }}>
                                {i.inc_desc}
                            </TableCell>
                            <TableCell size="medium" align="right">
                                {formatPrice(i.sub_total)}
                            </TableCell>
                            <TableCell size="medium" align="right">
                                {formatPrice(i.tax_total)}
                            </TableCell>
                            <TableCell size="medium" align="right">
                                {formatPrice(i.total)}
                            </TableCell>
                        </TableRow>
                    ))}
                    {incidentals.length > 0 ? (
                        <TableRow>
                            <IncTotalCell colSpan={3}>Total Charges & Credits</IncTotalCell>
                            <IncTotalCell colSpan={3} align="right">
                                {formatPrice(total_incidentals)}
                            </IncTotalCell>
                        </TableRow>
                    ) : (
                        <TableRow>
                            <IncEmptyCell size="medium" align="center" colSpan={6}>
                                No adjustments found
                            </IncEmptyCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

export function Sales(props) {
    const {
        gross_sales,
        gross_sales_online,
        gross_sales_pos,
        gross_sales_cash,
        discounts,
        net_sales,
    } = props;
    const [showGrossBreakdown, setShowGrossBreakdown] = useState(Number(gross_sales) !== 0);

    return (
        <Table>
            <TableBody>
                <TableRow>
                    <TableCellBold>
                        <BreakdownToggle
                            label="Gross Sales"
                            bold
                            expanded={showGrossBreakdown}
                            onToggle={() => setShowGrossBreakdown((shown) => !shown)}
                        >
                            Gross Sales
                        </BreakdownToggle>
                    </TableCellBold>
                    <TableCellBold align="right">{formatPrice(gross_sales)}</TableCellBold>
                </TableRow>
                {showGrossBreakdown && (
                    <>
                        <TableRow>
                            <TableCellLight>
                                <Box ml={2}>Online</Box>
                            </TableCellLight>
                            <TableCellLight align="right">
                                {formatPrice(gross_sales_online)}
                            </TableCellLight>
                        </TableRow>
                        <TableRow>
                            <TableCellLight>
                                <Box ml={2}>In-Person Card</Box>
                            </TableCellLight>
                            <TableCellLight align="right">
                                {formatPrice(gross_sales_pos)}
                            </TableCellLight>
                        </TableRow>
                        <TableRow>
                            <TableCellLight>
                                <Box ml={2}>In-Person Cash</Box>
                            </TableCellLight>
                            <TableCellLight align="right">
                                {formatPrice(gross_sales_cash)}
                            </TableCellLight>
                        </TableRow>
                    </>
                )}

                <TableRow>
                    <TableCell>Discounts</TableCell>
                    <TableCell align="right">
                        {formatPrice(discounts, { negative: true })}
                    </TableCell>
                </TableRow>

                <Separator divider />

                <TableRow>
                    <TableCellBold>Net Sales</TableCellBold>
                    <TableCellBold align="right">{formatPrice(net_sales)}</TableCellBold>
                </TableRow>
            </TableBody>
        </Table>
    );
}

export function Payments(props) {
    const {
        net_sales,
        tax,
        tax_online,
        tax_pos,
        tax_cash,
        tip,
        platform_fees_online,
        platform_fees,
        processing_fees_online,
        processing_fee,
        platform_fees_pos,
        processing_fees_pos,
        net_proceeds,
        total_collected,
    } = props;
    const [showTaxBreakdown, setShowTaxBreakdown] = useState(Number(tax) !== 0);
    const [showPlatformBreakdown, setShowPlatformBreakdown] = useState(Number(platform_fees) !== 0);
    const [showProcessingBreakdown, setShowProcessingBreakdown] = useState(
        Number(processing_fee) !== 0,
    );

    return (
        <Table>
            <TableBody>
                <TableRow>
                    <TableCell>Net Sales</TableCell>
                    <TableCell align="right">{formatPrice(net_sales)}</TableCell>
                </TableRow>
                <TableRow>
                    <TableCell>
                        <BreakdownToggle
                            label="Taxes Collected"
                            expanded={showTaxBreakdown}
                            onToggle={() => setShowTaxBreakdown((shown) => !shown)}
                        >
                            Taxes Collected
                        </BreakdownToggle>
                    </TableCell>
                    <TableCell align="right">{formatPrice(tax)}</TableCell>
                </TableRow>

                {showTaxBreakdown ? (
                    <>
                        <TableRow>
                            <TableCellLight>
                                <Box ml={2}>Online</Box>
                            </TableCellLight>
                            <TableCellLight align="right">{formatPrice(tax_online)}</TableCellLight>
                        </TableRow>
                        <TableRow>
                            <TableCellLight>
                                <Box ml={2}>In-Person Card</Box>
                            </TableCellLight>
                            <TableCellLight align="right">{formatPrice(tax_pos)}</TableCellLight>
                        </TableRow>
                        <TableRow>
                            <TableCellLight>
                                <Box ml={2}>In-Person Cash</Box>
                            </TableCellLight>
                            <TableCellLight align="right">{formatPrice(tax_cash)}</TableCellLight>
                        </TableRow>
                    </>
                ) : null}

                <TableRow>
                    <TableCell>In-Person Tips</TableCell>
                    <TableCell align="right">{formatPrice(tip)}</TableCell>
                </TableRow>

                <Separator divider />

                <TableRow>
                    <TableCellBold>Total Collected</TableCellBold>
                    <TableCellBold align="right">{formatPrice(total_collected)}</TableCellBold>
                </TableRow>

                <Separator />

                <TableRow>
                    <TableCell>
                        <BreakdownToggle
                            label="FrontDoor+ Fees"
                            expanded={showPlatformBreakdown}
                            onToggle={() => setShowPlatformBreakdown((shown) => !shown)}
                        >
                            FrontDoor+ Fees
                        </BreakdownToggle>
                    </TableCell>
                    <TableCell align="right">
                        {formatPrice(platform_fees, { negative: true })}
                    </TableCell>
                </TableRow>
                {showPlatformBreakdown && (
                    <>
                        <TableRow>
                            <TableCellLight>
                                <Box ml={2}>Online</Box>
                            </TableCellLight>
                            <TableCellLight align="right">
                                {formatPrice(platform_fees_online, { negative: true })}
                            </TableCellLight>
                        </TableRow>
                        <TableRow>
                            <TableCellLight>
                                <Box ml={2}>In-Person</Box>
                            </TableCellLight>
                            <TableCellLight align="right">
                                {formatPrice(platform_fees_pos, { negative: true })}
                            </TableCellLight>
                        </TableRow>
                    </>
                )}

                <TableRow>
                    <TableCell>
                        <BreakdownToggle
                            label="Payment Processing Fees"
                            expanded={showProcessingBreakdown}
                            onToggle={() => setShowProcessingBreakdown((shown) => !shown)}
                        >
                            Payment Processing Fees
                        </BreakdownToggle>
                    </TableCell>
                    <TableCell align="right">
                        {formatPrice(processing_fee, { negative: true })}
                    </TableCell>
                </TableRow>
                {showProcessingBreakdown && (
                    <>
                        <TableRow>
                            <TableCellLight>
                                <Box ml={2}>Online</Box>
                            </TableCellLight>
                            <TableCellLight align="right">
                                {formatPrice(processing_fees_online, { negative: true })}
                            </TableCellLight>
                        </TableRow>
                        <TableRow>
                            <TableCellLight>
                                <Box ml={2}>In-Person</Box>
                            </TableCellLight>
                            <TableCellLight align="right">
                                {formatPrice(processing_fees_pos, { negative: true })}
                            </TableCellLight>
                        </TableRow>
                    </>
                )}

                <Separator divider />

                <TableRow>
                    <TableCellBold>
                        Net Proceeds{' '}
                        <InfoTooltip message="Total collected after applicable fees. Includes cash collected by you." />
                    </TableCellBold>
                    <TableCellBold align="right">{formatPrice(net_proceeds)}</TableCellBold>
                </TableRow>
            </TableBody>
        </Table>
    );
}

export function Payouts(props) {
    const {
        net_proceeds,
        cash_collected,
        gross_sales_cash,
        tax_cash,
        incidental_total,
        total_payout,
        scheduled_payout_flag,
        payout_reserve_percent,
        reserve,
        total_payouts,
        remaining_payout,
        connect_type_id,
        dateRangeApplied,
        estimated = false,
    } = props;

    const [showCashBreakdown, setShowCashBreakdown] = useState(Number(cash_collected) !== 0);

    if (connect_type_id === 3 || dateRangeApplied) {
        return null;
    }

    const reservePercentText = parseDecimalToPercent(payout_reserve_percent, 2) + '%';

    return (
        <Table>
            <TableBody>
                <TableRow>
                    <TableCell>
                        Net Proceeds{' '}
                        <InfoTooltip message="Total collected after applicable fees. Includes cash collected by you." />
                    </TableCell>
                    <TableCell align="right">{formatPrice(net_proceeds)}</TableCell>
                </TableRow>
                <TableRow>
                    <TableCell>
                        <BreakdownToggle
                            label="Cash Collected by You"
                            expanded={showCashBreakdown}
                            onToggle={() => setShowCashBreakdown((shown) => !shown)}
                        >
                            Cash Collected by You
                        </BreakdownToggle>
                        <InfoTooltip message="This cash is already with your organization, so it is excluded from your payout." />
                    </TableCell>
                    <TableCell align="right">
                        {formatPrice(cash_collected, { negative: true })}
                    </TableCell>
                </TableRow>
                {showCashBreakdown && (
                    <>
                        <TableRow>
                            <TableCellLight>
                                <Box ml={2}>Cash</Box>
                            </TableCellLight>
                            <TableCellLight align="right">
                                {formatPrice(gross_sales_cash, { negative: true })}
                            </TableCellLight>
                        </TableRow>
                        <TableRow>
                            <TableCellLight>
                                <Box ml={2}>Cash (Tax)</Box>
                            </TableCellLight>
                            <TableCellLight align="right">
                                {formatPrice(tax_cash, { negative: true })}
                            </TableCellLight>
                        </TableRow>
                    </>
                )}
                <TableRow>
                    <TableCell>Additional Charges & Credits</TableCell>
                    <TableCell align="right">
                        {formatPrice(incidental_total, { negative: true })}
                    </TableCell>
                </TableRow>

                <Separator divider />

                <TableRow>
                    <TableCellBold>Total Payout Amount</TableCellBold>
                    <TableCellBold align="right">{formatPrice(total_payout)}</TableCellBold>
                </TableRow>

                <>
                    {scheduled_payout_flag ? (
                        <>
                            <Separator />

                            <TableRow>
                                <TableCell>
                                    Reserve Held ({reservePercentText}){' '}
                                    <InfoTooltip message="A percentage of funds is temporarily held back to mitigate potential financial risks. This amount will be fully released with your final payout." />
                                </TableCell>
                                <TableCell align="right">
                                    {formatPrice(reserve, { negative: true })}
                                </TableCell>
                            </TableRow>
                        </>
                    ) : (
                        <Separator />
                    )}
                    <TableRow>
                        <TableCell>Already Paid</TableCell>
                        <TableCell align="right">
                            {formatPrice(total_payouts, { negative: true })}
                        </TableCell>
                    </TableRow>

                    <Separator divider />

                    <TableRow>
                        <TableCellBold>
                            {estimated ? 'Estimated Remaining Payout' : 'Remaining Payout'}{' '}
                            <InfoTooltip
                                message={
                                    reserve
                                        ? 'The estimated amount available for payout after previous payouts and any reserve held. Funds held in reserve will be released with your final payout.'
                                        : 'The estimated amount available for payout after previous payouts.'
                                }
                            />
                        </TableCellBold>
                        <TableCellBold align="right">{formatPrice(remaining_payout)}</TableCellBold>
                    </TableRow>
                </>
            </TableBody>
        </Table>
    );
}

export function PromotionsAndComps({ bundle_savings, comps }) {
    return (
        <Table>
            <TableBody>
                <TableRow>
                    <TableCell>
                        Bundle Discounts{' '}
                        <InfoTooltip message="Savings provided through bundle pricing. Sales totals already reflect these savings." />
                    </TableCell>
                    <TableCell align="right">{formatPrice(bundle_savings)}</TableCell>
                </TableRow>
                <TableRow>
                    <TableCell>
                        Complimentary Ticket Value{' '}
                        <InfoTooltip message="Regular ticket value of complimentary tickets issued. This amount is excluded from sales totals." />
                    </TableCell>
                    <TableCell align="right">{formatPrice(comps)}</TableCell>
                </TableRow>
            </TableBody>
        </Table>
    );
}

export function StripeFeeEstimate({ cc_fee_order_pct, cc_fee_order_amt }) {
    return (
        <Typography variant="caption">
            FrontDoor+ estimates Stripe fees at {parseDecimalToPercent(cc_fee_order_pct, 2)}% +{' '}
            {formatPrice(cc_fee_order_amt)} per online order. Actual Stripe charges may differ
            slightly depending on card type, currency conversion, or discounts provided by Stripe.
            If something does not look right, we are happy to help.
        </Typography>
    );
}

function BreakdownToggle({ label, expanded, onToggle, children, bold = false }) {
    return (
        <ButtonBase
            onClick={onToggle}
            aria-label={`${expanded ? 'Hide' : 'Show'} ${label} breakdown`}
            aria-expanded={expanded}
            disableRipple
            sx={{
                font: 'inherit',
                color: 'inherit',
                lineHeight: 'inherit',
                verticalAlign: 'top',
                p: 0,
                gap: 0.5,
                '& svg': { stroke: 'currentColor', strokeWidth: bold ? 0.4 : 0 },
                '&.Mui-focusVisible': { outline: '2px solid currentColor', outlineOffset: 2 },
            }}
        >
            <Box component="span">{children}</Box>
            {expanded ? (
                <KeyboardArrowUpIcon fontSize="inherit" />
            ) : (
                <KeyboardArrowDownIcon fontSize="inherit" />
            )}
        </ButtonBase>
    );
}

function Separator(props) {
    return (
        <TableRow>
            <TableCell colSpan={props.columns || 2}>{props.divider && <Divider />}</TableCell>
        </TableRow>
    );
}

const formatPrice = (num, options) => {
    if (num === null || num === undefined || !Number.isFinite(Number(num))) return '-';
    const value = Number(num);
    const negative = value !== 0 && (options?.negative ? value > 0 : value < 0);
    return (
        (negative ? '-' : '') +
        '$' +
        Math.abs(value).toLocaleString(undefined, {
            minimumFractionDigits: options?.minimumFractionDigits ?? 2,
            maximumFractionDigits: options?.maximumFractionDigits ?? 2,
        })
    );
};

// Cells inherit color because parent will change opacity when reloading

const TableCell = styled(_TableCell)({
    border: 'none',
    color: 'inherit',
});
TableCell.defaultProps = { size: 'small' };

const TableCellBold = styled(_TableCell)({
    border: 'none',
    fontWeight: 'bold',
    color: 'inherit',
});
TableCellBold.defaultProps = { size: 'small' };

const TableCellLight = styled(_TableCell)(({ theme }) => ({
    border: 'none',
    color: theme.palette.grey[600],
    fontSize: '12px',
    paddingTop: 0,
}));
TableCellLight.defaultProps = { size: 'small' };

const TableCellHeader = styled(_TableCell)(({ theme }) => ({
    ...theme.typography.subHeader,
    paddingTop: 0,
    fontWeight: 'bold',
    borderBottom: '0.5px solid',
    color: 'inherit',
}));

const IncTotalCell = styled(_TableCell)({
    border: 'none',
    borderTop: '0.5px solid',
    paddingBottom: 0,
    fontWeight: 'bold',
    color: 'inherit',
});

const IncEmptyCell = styled(_TableCell)({
    border: 'none',
    paddingBottom: 0,
    color: 'inherit',
});
